import { useCallback } from 'react';
import {
  initializeCXoneSDK,
  startAgentSession,
  SessionConflictError,
  VoicePreference,
} from '../../services/cxone-sdk.service';
import { logger } from '../../utils/logger';
import { shouldShowVoicePreferenceSelector } from '../../utils/config';
import { CXoneState, CxoneConnectionStep } from '../../types';
import { connectCxoneVoiceIfNeeded } from './cxone-voice-connect';
import { CXONE_CONNECTION_STEPS, getCxoneConnectionSteps } from './cxone-connection-steps';
import { CxoneSessionRefs } from './types';
import {
  buildCxoneInitFailureState,
  buildCxoneInitSuccessState,
  buildCxoneSessionConflictState,
  buildCxoneVoiceConnectFailureState,
} from './cxone-init-outcomes.util';

interface UseCxoneSessionInitOptions {
  accessToken: string | null;
  cxoneAgentId?: string;
  audioRef: React.RefObject<HTMLAudioElement>;
  refs: CxoneSessionRefs;
  setState: React.Dispatch<React.SetStateAction<CXoneState>>;
  setVoicePreference: React.Dispatch<React.SetStateAction<VoicePreference | null>>;
  setHasSessionConflict: React.Dispatch<React.SetStateAction<boolean>>;
  setNeedsVoicePreference: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCxoneSessionInit({
  accessToken,
  cxoneAgentId,
  audioRef,
  refs,
  setState,
  setVoicePreference,
  setHasSessionConflict,
  setNeedsVoicePreference,
}: UseCxoneSessionInitOptions) {
  const { initializedRef, initializingRef, voicePreferenceRef, previousTokenRef } = refs;

  return useCallback(
    async (preference: VoicePreference) => {
      if (!accessToken || !cxoneAgentId) {
        logger.warn('[CXone] Cannot initialize: missing credentials');
        return;
      }

      if (initializedRef.current || initializingRef.current) {
        logger.warn('[CXone] Already initialized or connecting');
        return;
      }

      voicePreferenceRef.current = preference;
      setVoicePreference(preference);
      setNeedsVoicePreference(false);
      initializingRef.current = true;

      const steps = getCxoneConnectionSteps(preference);
      const setActiveStep = (activeStep: CxoneConnectionStep) => {
        setState((prev) => ({
          ...prev,
          isConnecting: true,
          connectionError: null,
          connectionProgress: { steps, activeStep },
        }));
      };

      setActiveStep(CXONE_CONNECTION_STEPS.initializingSdk);

      try {
        logger.lifecycle('[CXone] Initialization started', { voicePreference: preference.type });

        await initializeCXoneSDK(accessToken);

        setActiveStep(CXONE_CONNECTION_STEPS.startingSession);
        await startAgentSession(preference);

        if (preference.type === 'softphone') {
          setActiveStep(CXONE_CONNECTION_STEPS.connectingVoice);
        }

        const voiceConnectError = await connectCxoneVoiceIfNeeded(
          preference,
          cxoneAgentId,
          audioRef
        );

        if (voiceConnectError) {
          logger.warn('[CXone] Cannot connect voice client:', voiceConnectError);
          initializedRef.current = false;
          initializingRef.current = false;
          if (shouldShowVoicePreferenceSelector()) {
            setNeedsVoicePreference(true);
          }
          setState(buildCxoneVoiceConnectFailureState(voiceConnectError));
          return;
        }

        initializedRef.current = true;
        initializingRef.current = false;
        setState(buildCxoneInitSuccessState());
        previousTokenRef.current = accessToken;
        logger.lifecycle('[CXone] Initialization complete');
      } catch (error) {
        if (error instanceof SessionConflictError) {
          logger.warn('[CXone] Session conflict detected:', error.message);
          setHasSessionConflict(true);
          initializingRef.current = false;
          setState(buildCxoneSessionConflictState(error.message));
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error('[CXone] Initialization failed:', errorMessage);
        initializingRef.current = false;
        if (shouldShowVoicePreferenceSelector()) {
          setNeedsVoicePreference(true);
        }
        setState(buildCxoneInitFailureState(errorMessage));
      }
    },
    [
      accessToken,
      cxoneAgentId,
      audioRef,
      initializedRef,
      initializingRef,
      voicePreferenceRef,
      previousTokenRef,
      setState,
      setVoicePreference,
      setHasSessionConflict,
      setNeedsVoicePreference,
    ]
  );
}
